import { useState, useEffect } from "react";
import { UPDATES_DATA } from "../../constant/UpdatesData";
import { FaCertificate } from "react-icons/fa";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useMotionTemplate,
  useTransform,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import Eyebrow from "../common/Eyebrow";
import Reveal from "../common/Reveal";

const ISO_CERTIFICATES = UPDATES_DATA.filter(
  (item) => item.type === "Certification"
);

const TiltShowcase = ({ item }) => {
  const prefersReducedMotion = useReducedMotion();
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);

  const rotateX = useSpring(useTransform(py, [0, 1], [10, -10]), {
    stiffness: 200,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(px, [0, 1], [-10, 10]), {
    stiffness: 200,
    damping: 20,
  });
  const spotlight = useMotionTemplate`radial-gradient(420px circle at ${useTransform(
    px,
    [0, 1],
    ["0%", "100%"]
  )} ${useTransform(py, [0, 1], ["0%", "100%"])}, rgba(var(--website-secondary-rgb),0.28), transparent 70%)`;

  const handleMouseMove = (e) => {
    // Keep the cursor spotlight, drop the 3D tilt under reduced motion.
    if (prefersReducedMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width);
    py.set((e.clientY - rect.top) / rect.height);
  };
  const handleMouseLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return (
    <div style={{ perspective: 1400 }} className="relative">
      {/* animated shimmering gradient border */}
      <div className="relative overflow-hidden rounded-[28px] p-[2px]">
        <motion.div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, var(--website-secondary-color), var(--website-primary-color) 45%, var(--website-secondary-color) 55%, var(--website-primary-color))",
            backgroundSize: "300% 300%",
          }}
          animate={
            prefersReducedMotion
              ? undefined
              : { backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }
          }
          transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
        />

        <motion.div
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          className="group relative overflow-hidden rounded-[26px] bg-[var(--surface-raised)] p-5"
        >
          {/* cursor spotlight */}
          <motion.div
            className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ background: spotlight }}
          />

          {/* corner ribbon */}
          <div className="pointer-events-none absolute left-[-46px] top-[18px] z-20 w-[170px] rotate-[-45deg] bg-[var(--website-secondary-color)] py-1 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--on-secondary)] shadow-lg">
            Certified
          </div>

          {/* floating badge */}
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 18,
              delay: 0.2,
            }}
            style={{ transform: "translateZ(70px)" }}
            className="absolute -bottom-3 -right-3 z-20 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--website-secondary-color)] text-[var(--on-secondary)] shadow-[0_0_25px_rgba(var(--website-secondary-rgb),0.6)]"
          >
            <FaCertificate size={26} />
          </motion.div>

          <div
            style={{ transform: "translateZ(30px)" }}
            className="relative flex justify-center overflow-hidden rounded-2xl bg-white p-3"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={item.id}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                src={require(`../../assets/NewUpdates/${item.imgSrc}`)}
                alt={item.title}
                className="h-72 w-full rounded-lg object-contain md:h-80"
                loading="lazy"
              />
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// `seamless` drops the section's own backdrop so it can sit inside a shared
// dark band (see the home page) without repeating the texture and glows.
const UpdatesSection = ({ seamless = false }) => {
  const sectionReducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % ISO_CERTIFICATES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const current = ISO_CERTIFICATES[index];

  return (
    <section
      className={
        seamless
          ? "relative border-t border-white/[0.06] px-6 py-20"
          : "relative overflow-hidden bg-[var(--surface-dark)] px-6 py-24"
      }
    >
      {!seamless && (
        <>
          {/* dot-grid texture */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #ffffff 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />

          {/* drifting glow orbs */}
          <motion.div
            className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-[rgba(var(--website-secondary-rgb),0.2)] blur-3xl"
            animate={
              sectionReducedMotion ? undefined : { y: [0, 30, 0], x: [0, 20, 0] }
            }
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl"
            animate={
              sectionReducedMotion
                ? undefined
                : { y: [0, -30, 0], x: [0, -20, 0] }
            }
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      )}

      <div className="relative mx-auto max-w-6xl">
        <Reveal>
          <div className="grid gap-12 md:grid-cols-[1.1fr_0.9fr] md:items-center">
            <div className="order-1 md:col-start-1 md:row-start-1">
              <Eyebrow className="text-[var(--website-secondary-color)]">
                Updates &amp; highlights
              </Eyebrow>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-4xl">
                Proof of{" "}
                <span className="bg-gradient-to-r from-[var(--website-secondary-color)] to-teal-300 bg-clip-text text-transparent">
                  continuous improvement.
                </span>
              </h2>
              <div className="mt-8 min-h-[240px] transition-[min-height] duration-300 ease-in-out md:min-h-[200px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  >
                    <Eyebrow className="tracking-[0.18em] text-slate-400">
                      {current.type}
                    </Eyebrow>

                    <h3 className="mt-3 text-2xl font-semibold text-white">
                      {current.title}
                    </h3>
                    <p className="mt-3 max-w-xl leading-relaxed text-slate-300">
                      {current.description}
                    </p>
                    <p className="mt-3 text-sm text-slate-500">
                      {current.date}
                    </p>
                    {current.file && (
                      <a
                        href={current.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 inline-flex rounded-full bg-[var(--website-secondary-color)] px-5 py-3 text-sm font-semibold text-[var(--on-secondary)] shadow-[0_0_20px_rgba(var(--website-secondary-rgb),0.35)] transition hover:shadow-[0_0_30px_rgba(var(--website-secondary-rgb),0.55)]"
                      >
                        View credential
                      </a>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-8 flex items-center gap-3">
                {ISO_CERTIFICATES.map((cert, i) => (
                  <button
                    key={cert.id}
                    onClick={() => setIndex(i)}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wide transition ${
                      i === index
                        ? "border-[var(--website-secondary-color)] bg-[rgba(var(--website-secondary-rgb),0.1)] text-[var(--website-secondary-color)]"
                        : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full transition ${
                        i === index
                          ? "bg-[var(--website-secondary-color)] shadow-[0_0_8px_rgba(var(--website-secondary-rgb),0.8)]"
                          : "bg-white/30"
                      }`}
                    />
                    {cert.title.split(":")[0]}
                  </button>
                ))}
              </div>
            </div>
            <div className="order-2 md:col-start-2 md:row-start-1">
              <TiltShowcase item={current} />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default UpdatesSection;
